import { useEffect, useMemo, useState } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { useOrders } from '../../hooks/useOrders';
import { useAuth } from '../../contexts/AuthContext';
import {
  tenantsService,
  TenantSummary,
  CreateTenantDto,
} from '../../services/tenants.service';
import {
  Building2,
  Plus,
  RefreshCw,
  Search,
  Store,
  Users,
  ShoppingBag,
  Boxes,
  Globe,
  Mail,
  Phone,
  MapPin,
  X,
  Loader2,
  AlertCircle,
  CheckCircle2,
  KeyRound,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

interface TenantsManagementPageProps {
  onNavigate: (page: string) => void;
}

const DEFAULT_FORM: CreateTenantDto = {
  name: '',
  description: '',
  email: '',
  adminEmail: '',
  adminPassword: '',
  adminFirstName: 'Tenant',
  adminLastName: 'Admin',
  phone: '',
  address: '',
  website: '',
  timezone: 'Africa/Nairobi',
  currency: 'KES',
  taxRate: 16,
  vatInclusive: true,
  defaultPrintFormat: 'standard',
  theme: 'light',
  isActive: true,
  isRestaurant: false,
  orderByTable: false,
};

export function TenantsManagementPage({ onNavigate }: TenantsManagementPageProps) {
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true
  );
  const { user } = useAuth();
  const { orders: ordersData, total: ordersTotal } = useOrders({ page: 1, limit: 50 });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState<CreateTenantDto>(DEFAULT_FORM);
  const [resetTarget, setResetTarget] = useState<TenantSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TenantSummary | null>(null);
  const [newRootPassword, setNewRootPassword] = useState('');

  const canManageTenants = Boolean(user?.permissions?.includes('tenants_platform'));
  const ordersCount = ordersTotal > 0 ? ordersTotal : ordersData.length;

  const loadTenants = async (nextSearch = appliedSearch) => {
    setLoading(true);
    setError('');

    try {
      const response = await tenantsService.getTenants({ page: 1, limit: 100, search: nextSearch || undefined });
      const payload: any = response.data;

      // Normalize possible shapes:
      // 1) { data: Tenant[], meta }
      // 2) { data: { data: Tenant[], meta } }
      // 3) Tenant[]
      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.data?.data)
            ? payload.data.data
            : [];

      const meta = payload?.meta || payload?.data?.meta;

      setTenants(rows);
      setTotal(Number(meta?.total) || rows.length || 0);
    } catch (err: any) {
      const message = err?.message || 'Failed to load tenants';
      setError(message);
      toast.error(message);
      setTenants([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  const stats = useMemo(() => {
    return tenants.reduce(
      (acc, tenant) => {
        acc.active += tenant.isActive ? 1 : 0;
        acc.branches += tenant._count?.branches || tenant.branches.length;
        acc.users += tenant._count?.users || 0;
        return acc;
      },
      { active: 0, branches: 0, users: 0 },
    );
  }, [tenants]);

  const updateForm = <K extends keyof CreateTenantDto>(key: K, value: CreateTenantDto[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleCreateTenant = async () => {
    if (!form.name?.trim()) {
      toast.error('Tenant name is required');
      return;
    }

    if (!form.adminPassword?.trim()) {
      toast.error('Default tenant admin password is required');
      return;
    }

    const effectiveAdminEmail = (form.adminEmail || form.email || '').trim();
    if (!effectiveAdminEmail) {
      toast.error('Tenant admin email is required (admin email or tenant email)');
      return;
    }

    setSaving(true);
    try {
      await tenantsService.createTenant({
        ...form,
        name: form.name.trim(),
        description: form.description?.trim() || undefined,
        email: form.email?.trim() || undefined,
        adminEmail: effectiveAdminEmail,
        adminPassword: form.adminPassword?.trim(),
        adminFirstName: form.adminFirstName?.trim() || 'Tenant',
        adminLastName: form.adminLastName?.trim() || 'Admin',
        phone: form.phone?.trim() || undefined,
        address: form.address?.trim() || undefined,
        website: form.website?.trim() || undefined,
      });

      toast.success('Tenant created successfully. Main Branch was created automatically.');
      setShowCreateModal(false);
      setForm(DEFAULT_FORM);
      await loadTenants(appliedSearch);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create tenant');
    } finally {
      setSaving(false);
    }
  };

  const handleResetRootAdminPassword = async () => {
    if (!resetTarget) return;
    if (!newRootPassword.trim()) {
      toast.error('New password is required');
      return;
    }

    setSaving(true);
    try {
      const response = await tenantsService.resetRootAdminPassword(resetTarget.id, newRootPassword.trim());
      toast.success(response.data?.message || 'Tenant admin password reset successfully');
      setResetTarget(null);
      setNewRootPassword('');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to reset tenant admin password');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTenant = async () => {
    if (!deleteTarget) return;

    setSaving(true);
    try {
      const response = await tenantsService.deleteTenant(deleteTarget.id);
      toast.success(response.data?.message || 'Tenant deleted successfully');
      setDeleteTarget(null);
      await loadTenants(appliedSearch);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete tenant');
    } finally {
      setSaving(false);
    }
  };

  if (!canManageTenants) {
    return (
      <div className="flex h-screen bg-gray-50 dark:bg-gray-950 overflow-hidden">
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          activePage="tenants"
          onNavigate={onNavigate}
          ordersCount={ordersCount}
        />
        <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'}`}>
          <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} />
          <main className="flex-1 overflow-y-auto p-6">
            <div className="max-w-[900px] mx-auto bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-8 text-center">
              <Building2 className="w-10 h-10 mx-auto mb-3 text-amber-500" />
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Access Restricted</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">You need the platform tenant permission to manage tenants.</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950 overflow-hidden">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activePage="tenants"
        onNavigate={onNavigate}
        ordersCount={ordersCount}
      />

      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'}`}>
        <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between mb-6 gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                  <Building2 className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tenant Management</h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Super admin view of all tenants and their active branches</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => loadTenants(appliedSearch)}
                  disabled={loading}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                  title="Refresh tenants"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">New Tenant</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Tenants', value: total, icon: Building2, bg: 'bg-indigo-50 dark:bg-indigo-900/30', ic: 'text-indigo-600 dark:text-indigo-400' },
                { label: 'Active', value: stats.active, icon: CheckCircle2, bg: 'bg-green-50 dark:bg-green-900/30', ic: 'text-green-600 dark:text-green-400' },
                { label: 'Branches', value: stats.branches, icon: Store, bg: 'bg-blue-50 dark:bg-blue-900/30', ic: 'text-blue-600 dark:text-blue-400' },
                { label: 'Users', value: stats.users, icon: Users, bg: 'bg-purple-50 dark:bg-purple-900/30', ic: 'text-purple-600 dark:text-purple-400' },
              ].map((card) => (
                <div key={card.label} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 flex items-center gap-3">
                  <div className={`p-2.5 rounded-lg ${card.bg}`}>
                    <card.icon className={`w-5 h-5 ${card.ic}`} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{card.label}</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{card.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search tenant by name or email"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setAppliedSearch(search.trim());
                        loadTenants(search.trim());
                      }
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={() => {
                    setAppliedSearch(search.trim());
                    loadTenants(search.trim());
                  }}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
                >
                  Search
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-3 p-4 mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {loading ? (
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-12 flex items-center justify-center gap-3 text-gray-600 dark:text-gray-300">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Loading tenants...</span>
              </div>
            ) : tenants.length === 0 ? (
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-12 text-center">
                <Building2 className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-gray-600 dark:text-gray-400 font-medium">No tenants found</p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">Create a tenant to provision a new restaurant workspace with a default Main Branch.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                {tenants.map((tenant) => (
                  <div key={tenant.id} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                    <div className="p-5 border-b border-gray-200 dark:border-gray-800">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">{tenant.name}</h2>
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${tenant.isActive ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400'}`}>
                              {tenant.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          {tenant.description && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{tenant.description}</p>
                          )}
                        </div>
                        <div className="text-right text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          Created {new Date(tenant.createdAt).toLocaleDateString()}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                        {tenant.email && (
                          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 min-w-0">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span className="truncate">{tenant.email}</span>
                          </div>
                        )}
                        {tenant.phone && (
                          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 min-w-0">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span className="truncate">{tenant.phone}</span>
                          </div>
                        )}
                        {tenant.address && (
                          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 min-w-0 col-span-2">
                            <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="truncate">{tenant.address}</span>
                          </div>
                        )}
                        {tenant.website && (
                          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 min-w-0 col-span-2">
                            <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="truncate">{tenant.website}</span>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
                        {[
                          { label: 'Users', value: tenant._count?.users || 0, icon: Users },
                          { label: 'Branches', value: tenant._count?.branches || tenant.branches.length, icon: Store },
                          { label: 'Products', value: tenant._count?.products || 0, icon: Boxes },
                          { label: 'Orders', value: tenant._count?.orders || 0, icon: ShoppingBag },
                        ].map((item) => (
                          <div key={item.label} className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                              <item.icon className="w-3.5 h-3.5" />
                              <span>{item.label}</span>
                            </div>
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">{item.value}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Active Branches</h3>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{tenant.branches.length} branch{tenant.branches.length !== 1 ? 'es' : ''}</span>
                      </div>

                      <div className="space-y-2">
                        {tenant.branches.map((branch) => (
                          <div key={branch.id} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{branch.name}</p>
                                {branch.isMain && (
                                  <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400">
                                    Main
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400">Code: {branch.code}</p>
                            </div>
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${branch.isActive ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400'}`}>
                              {branch.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-2">
                        <button
                          onClick={() => {
                            setResetTarget(tenant);
                            setNewRootPassword('');
                          }}
                          className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/50"
                        >
                          <KeyRound className="w-3.5 h-3.5" />
                          Reset Tenant Admin Password
                        </button>
                        <button
                          onClick={() => setDeleteTarget(tenant)}
                          className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete Tenant
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Create Tenant</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">A default Main Branch will be created automatically by the backend.</p>
              </div>
              <button onClick={() => setShowCreateModal(false)} className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tenant Name</label>
                <input value={form.name || ''} onChange={(e) => updateForm('name', e.target.value)} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm" placeholder="Qrypt Restaurant Group" />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
                <textarea value={form.description || ''} onChange={(e) => updateForm('description', e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm" placeholder="Optional description" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email</label>
                <input value={form.email || ''} onChange={(e) => updateForm('email', e.target.value)} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm" placeholder="admin@tenant.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tenant Admin Email</label>
                <input value={form.adminEmail || ''} onChange={(e) => updateForm('adminEmail', e.target.value)} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm" placeholder="tenant-admin@tenant.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tenant Admin First Name</label>
                <input value={form.adminFirstName || ''} onChange={(e) => updateForm('adminFirstName', e.target.value)} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm" placeholder="Tenant" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tenant Admin Last Name</label>
                <input value={form.adminLastName || ''} onChange={(e) => updateForm('adminLastName', e.target.value)} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm" placeholder="Admin" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Default Tenant Admin Password</label>
                <input type="password" value={form.adminPassword || ''} onChange={(e) => updateForm('adminPassword', e.target.value)} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm" placeholder="Minimum 8 characters" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone</label>
                <input value={form.phone || ''} onChange={(e) => updateForm('phone', e.target.value)} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm" placeholder="+254..." />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Address</label>
                <input value={form.address || ''} onChange={(e) => updateForm('address', e.target.value)} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm" placeholder="City, street, country" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Website</label>
                <input value={form.website || ''} onChange={(e) => updateForm('website', e.target.value)} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm" placeholder="https://example.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Timezone</label>
                <input value={form.timezone || ''} onChange={(e) => updateForm('timezone', e.target.value)} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm" placeholder="Africa/Nairobi" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Currency</label>
                <input value={form.currency || ''} onChange={(e) => updateForm('currency', e.target.value)} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm" placeholder="KES" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tax Rate (%)</label>
                <input type="number" value={form.taxRate ?? 0} onChange={(e) => updateForm('taxRate', Number(e.target.value))} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Theme</label>
                <select value={form.theme || 'light'} onChange={(e) => updateForm('theme', e.target.value as 'light' | 'dark')} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm">
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Default Print Format</label>
                <select value={form.defaultPrintFormat || 'standard'} onChange={(e) => updateForm('defaultPrintFormat', e.target.value as 'standard' | 'thermal')} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm">
                  <option value="standard">Standard</option>
                  <option value="thermal">Thermal</option>
                </select>
              </div>

              <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={Boolean(form.vatInclusive)} onChange={(e) => updateForm('vatInclusive', e.target.checked)} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                Prices include VAT
              </label>

              <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={form.isActive ?? false} onChange={(e) => updateForm('isActive', e.target.checked)} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                Tenant active on creation
              </label>

              <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={form.isRestaurant ?? false}
                  onChange={(e) => {
                    updateForm('isRestaurant', e.target.checked);
                    if (!e.target.checked) updateForm('orderByTable', false);
                  }}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                Is a restaurant
              </label>

              <label className={`inline-flex items-center gap-2 text-sm ${form.isRestaurant ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-600 cursor-not-allowed'}`}>
                <input
                  type="checkbox"
                  checked={form.orderByTable ?? false}
                  onChange={(e) => updateForm('orderByTable', e.target.checked)}
                  disabled={!form.isRestaurant}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-40"
                />
                Enable order by table
                {!form.isRestaurant && <span className="text-xs text-gray-400 dark:text-gray-600">(requires restaurant)</span>}
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg">Cancel</button>
              <button onClick={handleCreateTenant} disabled={saving} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Create Tenant
              </button>
            </div>
          </div>
        </div>
      )}

      {resetTarget && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-5">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Reset Tenant Admin Password</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Tenant: {resetTarget.name}</p>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">New Password</label>
              <input
                type="password"
                value={newRootPassword}
                onChange={(e) => setNewRootPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm"
                placeholder="Minimum 8 characters"
              />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setResetTarget(null)} className="px-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg">Cancel</button>
              <button onClick={handleResetRootAdminPassword} disabled={saving} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-5">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Delete Tenant</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
              This will permanently delete <strong>{deleteTarget.name}</strong> and all related tenant data (branches, users, orders, products, customers and more).
            </p>
            <p className="text-xs text-red-600 dark:text-red-400 mt-2">This action cannot be undone.</p>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg">Cancel</button>
              <button onClick={handleDeleteTenant} disabled={saving} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Delete Tenant
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}